import { useRef, useState } from 'react'
import { WidgetShell } from './WidgetShell'

/**
 * Сетка панели.
 *
 * 12 колонок, размер гаджета — span 3/6/12. Свободного resize за уголок
 * нет намеренно: пресеты предсказуемы, работают на планшете и не требуют
 * верстать каждый виджет под произвольную ширину.
 *
 * Порядок хранится индексом в массиве, раскладку по строкам делает Grid.
 * grid-auto-flow: dense не используется — он переставляет гаджеты сам,
 * и человек перестаёт понимать, куда попадёт перетащенный блок.
 */
export function DashboardGrid({
  layout,
  catalog,
  results,
  loading,
  updatedAt,
  stale,
  canEdit,
  onLayoutChange,
  onSettings,
  onRetry,
}) {
  const [dragId, setDragId] = useState(null)
  const [overId, setOverId] = useState(null)
  const liveRef = useRef(null)

  const visible = layout.filter((w) => w.enabled)

  const announce = (msg) => {
    if (liveRef.current) liveRef.current.textContent = msg
  }

  const move = (widgetId, delta) => {
    const from = layout.findIndex((w) => w.id === widgetId)
    const to = from + delta
    if (from < 0 || to < 0 || to >= layout.length) return

    const next = [...layout]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    onLayoutChange(next)

    const spec = catalog[item.type]
    announce(`Гаджет «${spec?.title || item.type}» перемещён на позицию ${to + 1} из ${layout.length}`)
  }

  const reorder = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return
    const from = layout.findIndex((w) => w.id === sourceId)
    const to = layout.findIndex((w) => w.id === targetId)
    if (from < 0 || to < 0) return

    const next = [...layout]
    const [item] = next.splice(from, 1)
    next.splice(to, 0, item)
    onLayoutChange(next)
    announce(`Гаджет перемещён на позицию ${to + 1} из ${layout.length}`)
  }

  const patch = (widgetId, changes) => {
    onLayoutChange(layout.map((w) => (w.id === widgetId ? { ...w, ...changes } : w)))
  }

  const remove = (widgetId) => {
    onLayoutChange(layout.filter((w) => w.id !== widgetId))
  }

  const onKeyDown = (e, widgetId) => {
    // Ctrl+стрелки — перестановка с клавиатуры. Обычные стрелки не трогаем:
    // они нужны для прокрутки страницы.
    if (!canEdit || !e.ctrlKey) return
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      move(widgetId, -1)
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      move(widgetId, 1)
    }
  }

  if (!visible.length) {
    return (
      <>
        <div className="panel-empty">
          <p className="panel-empty__title">На панели пока ничего нет</p>
          <p className="panel-empty__hint">
            Добавьте гаджеты — они появятся здесь и начнут обновляться сами.
          </p>
        </div>
        <span ref={liveRef} className="visually-hidden" aria-live="polite" />
      </>
    )
  }

  return (
    <>
      <div className="dash-grid">
        {visible.map((w) => {
          const index = layout.findIndex((x) => x.id === w.id)
          return (
            <div
              key={w.id}
              className={`dash-grid__cell dash-grid__cell--${w.size} ${
                overId === w.id ? 'is-drop-target' : ''
              }`}
              onKeyDown={(e) => onKeyDown(e, w.id)}
            >
              <WidgetShell
                widget={w}
                spec={catalog[w.type]}
                result={results[w.id]}
                loading={loading}
                updatedAt={updatedAt}
                stale={stale}
                canEdit={canEdit}
                position={index}
                count={layout.length}
                isDragging={dragId === w.id}
                onResize={(size) => patch(w.id, { size })}
                onToggle={() => patch(w.id, { enabled: false })}
                onMove={(delta) => move(w.id, delta)}
                onSettings={() => onSettings(w)}
                onRemove={() => remove(w.id)}
                onRetry={onRetry}
                dragHandlers={{
                  onDragStart: (e) => {
                    setDragId(w.id)
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', w.id)
                  },
                  onDragEnd: () => {
                    setDragId(null)
                    setOverId(null)
                  },
                  onDragOver: (e) => {
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    setOverId(w.id)
                  },
                  onDragLeave: () => setOverId((cur) => (cur === w.id ? null : cur)),
                  onDrop: (e) => {
                    e.preventDefault()
                    reorder(e.dataTransfer.getData('text/plain') || dragId, w.id)
                    setDragId(null)
                    setOverId(null)
                  },
                }}
              />
            </div>
          )
        })}
      </div>
      <span ref={liveRef} className="visually-hidden" aria-live="polite" />
    </>
  )
}
